import { useState, useMemo, useEffect } from 'react';
import { toast, ToastContainer } from "react-toastify";
import { FaEdit, FaEye, FaTrash, FaThLarge, FaList, FaChartBar } from 'react-icons/fa';
import TanStackTable from '../../components/TanStackTable';
import { createColumnHelper } from '@tanstack/react-table';
import ActiveStatus from '../../components/Common/ActiveStatus';
import { ActionButton } from "../../components/Forms/ActionButton";
import { TextArea } from "../../components/Forms/TextArea";
import Heading from "../../components/Heading";
import InputField from "../../components/Forms/InputField";
import { challengeCategoryEditView, createChallengeCategory, updateChallengeCategory } from "../../api/challenge";
import 'react-toastify/dist/ReactToastify.css';
import { useLocation, useNavigate } from "react-router-dom";
import MediaUpload from "../../components/Forms/MediaUpload";
import { useAddIssueCategory, useDeleteIssueCategory, useEditIssueCategory, useGetIssueCategories } from '../../queries/helpDesk';

// const mockIssues = Array.from({ length: 100 }, (_, i) => ({
//   _id: i + 1,
//   icon: 'https://randomuser.me/api/portraits/men/1.jpg',
//   issueCategory: 'Issue Category',
//   status: true,
// }));

const IssueCategoryList = () => {
   // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    status: 'Active',
  });

  const {data: issueCategories, isLoading, isError} = useGetIssueCategories()


  const [filteredData, setFilteredData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [rowSelection, setRowSelection] = useState({});

  // Filtering logic
  useEffect(() => {
    // Get the actual array from the response
    const categoryList = Array.isArray(issueCategories) 
      ? issueCategories 
      : issueCategories?.data || issueCategories?.categories || [];
    
    let filtered = categoryList;
    if (search) {
      filtered = filtered.filter(issue =>
        issue.name?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (filters.status) {
      filtered = filtered.filter(issue =>
        (filters.status === 'Active' ? issue.isActive : !issue.isActive)
      );
    }
    setFilteredData(filtered);
    setPage(1); // Reset to first page on filter
  }, [search, filters, issueCategories]);

  // Status toggle handler
  const {mutate: updateIssueCategoryStatus} = useEditIssueCategory();
  const {mutate: deleteIssueCategory} = useDeleteIssueCategory();
  
  const handleStatusToggle = (id, newStatus) => {
    // Find the current issue to get its data
    const currentIssue = filteredData.find(issue => issue._id === id);
    if (!currentIssue) return;

    // Update the status using the API
    updateIssueCategoryStatus({
      categoryId: id,
      categoryData: {
        id: id,
        name: currentIssue.name,
        description: currentIssue.description,
        isActive: newStatus,
        imageUrl: currentIssue.imageUrl
      }
    }, {
      onSuccess: () => {
        toast.success('Status updated successfully!');
        // The data will be automatically refetched by react-query
      },
      onError: (error) => {
        toast.error('Failed to update status');
        console.error('Status update error:', error);
      }
    });
  };

  // Action handlers
  const handleEdit = (id) => {
    const categoryToEdit = filteredData.find(category => category._id === id);
    navigate('/help-desk/issue-categories', { state: categoryToEdit });
  };

  const handleView = (id) => {
    const categoryToView = filteredData.find(category => category._id === id);
    console.log('Viewing category:', categoryToView);
    // Add navigation to view page when available
    toast.info('View functionality - to be implemented');
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this issue category?')) {
      deleteIssueCategory(id, {
        onSuccess: () => {
          toast.success('Issue category deleted successfully!');
        },
        onError: (error) => {
          toast.error('Failed to delete issue category');
          console.error('Delete error:', error);
        }
      });
    }
  };

  // Table columns
  const columnHelper = createColumnHelper();
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'select',
      header: ({ table }) => (
        <input
          type="checkbox"
          className="w-4 h-4"
          checked={table.getIsAllRowsSelected()}
          onChange={table.getToggleAllRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          className="w-4 h-4"
          checked={row.getIsSelected()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    }),
    columnHelper.display({
      id: 'srno',
      header: 'Sr. No.',
      cell: info => (page - 1) * pageSize + info.row.index + 1,
    }),
    columnHelper.display({
      id: 'icons',
      header: 'Icons',
      cell: info => (
        <img src={info.row.original.imageUrl || 'https://via.placeholder.com/32'} alt="icon" className="w-8 h-8 rounded-full" />
      ),
    }),
    columnHelper.accessor('name', {
      header: 'Issue Category',
      cell: info => (
        <div className="max-w-sm" title={info.getValue()}>
          {info.getValue() || 'Lorem ipsum dolor sit amet, consectetur adipiscing elit aliqua...'}
        </div>
      ),
    }),
    columnHelper.display({
      id: 'status',
      header: 'Status',
      cell: info => (
        <div className="flex items-center gap-2">
          <ActiveStatus
            active={info.row.original.isActive}
            setActive={(newStatus) => handleStatusToggle(info.row.original._id, newStatus)}
            label=" "
            innerActiveText="Active"
            innerInactiveText="Inactive"
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: info => (
        <div className="flex items-center gap-2">
          {/* <button 
            className="bg-blue-100 text-blue-600 p-2 rounded hover:bg-blue-200 cursor-pointer" 
            title="Edit"
            onClick={(e) => {
              e.stopPropagation();
              handleEdit(info.row.original._id);
            }}
          >
            <FaEdit className="w-4 h-4" />
          </button> */}
          {/* <button 
            className="bg-green-100 text-green-600 p-2 rounded hover:bg-green-200 cursor-pointer" 
            title="View"
            onClick={(e) => {
              e.stopPropagation();
              handleView(info.row.original._id);
            }}
          >
            <FaEye className="w-4 h-4" />
          </button> */}
          <button 
            className="bg-red-100 text-red-600 p-2 rounded hover:bg-red-200 cursor-pointer" 
            title="Delete"
            onClick={(e) => {
              e.stopPropagation();
              handleDelete(info.row.original._id);
            }}
          >
            <FaTrash className="w-4 h-4" />
          </button>
        </div>
      ),
    }),
  ], [page, pageSize, handleStatusToggle, handleEdit, handleView, handleDelete]);

  // Pagination logic
  const paginatedData = filteredData.slice((page - 1) * pageSize, page * pageSize);
  const totalPages = Math.ceil(filteredData.length / pageSize);


  // Create Issue Category Form
    const { state } = useLocation();
    const navigate = useNavigate();
  
    const initialData = [
        { label: 'Name Of Category', placeholder: 'Enter Name Of Category', name: 'name', value: '', type: 'text' },
        { label: 'Description', placeholder: 'Enter Description Of Category', name: 'description', value: '', type: 'textarea' },
    ];

    const [ data, setData ] = useState(initialData);

    const [ active, setActive ] = useState(true);
    const [ image, setImage ] = useState(null);
    
    const {mutate: addIssueCategory} = useAddIssueCategory()
    const {mutate: updateIssueCategory} = useEditIssueCategory()
    const handleChange = (index, newValue) => {
        setData(prevFields => prevFields.map((field, i) => i === index ? { ...field, value: newValue } : field));
    }

    const resetForm = () => {
        setData(initialData);
        setActive(true);
        setImage(null);
    }
    
    // const getAndUpload = async (file) => {
    //     if (!file) return;
    //     const reader = new FileReader();
    //     reader.readAsDataURL(file);
    //     reader.onloadend = () => {
    //         setImage(reader.result);
    //     };
    // };
    
    const handleSubmit = e => {
        e.preventDefault();
        
        // Check if data exists
        if (!data || !data[0] || !data[1]) {
            toast.error('Form data is not available');
            return;
        }
        
        // Basic validation
        if (!data[0].value.trim()) {
            toast.error('Please enter a category name');
            return;
        }
        
        if (!data[1].value.trim()) {
            toast.error('Please enter a description');
            return;
        }

        if(state?._id){
            updateIssueCategory({
                categoryId: state._id,
                categoryData: {
                name: data[0].value,
                description: data[1].value,
                isActive: active,
                imageUrl: image
                }
            },
            {
                onSuccess: () => {
                    toast.success('Issue Category updated successfully');
                    resetForm();
                    navigate('/help-desk/issue-categories');
                }
            }
          );
        }
        else {
            addIssueCategory({
                name: data[0].value,
                description: data[1].value,
                isActive: active,
                imageUrl: image,
            },
            {
                onSuccess: () => {
                    toast.success('Issue Category created successfully');
                    resetForm();
                }
              });
        }
    }
    
    // useEffect(() => {
    //     if (image) {
    //         // setImage(image);
    //         getAndUpload(image);
    //     }
    //     else {
    //         setImage(null);
    //     }
    // }, [image]);
    
    useEffect(() => {
        if(state?._id) challengeCategoryEditView(state._id, data, setData, setActive, setImage, toast);
        // eslint-disable-next-line
    }, []);

  return (
    <div>
      <form className="flex flex-col w-full font-semibold ">
            <Heading
                text={"Create New Issue Category"}
            />

            <section className="flex flex-col justify-center px-5 pt-2 py-9 gap-3 w-full bg-white rounded-md shadow-[0px_4px_25px_rgba(94,92,154,0.1)]">

                <div className="flex w-full gap-3">
                    <div className="w-full mt-2.5">
                        <div className="flex flex-wrap gap-5 items-end w-full justify-between">
                            {data && data[0] && (
                            <InputField
                                label={data[0].label}
                                placeholder={data[0].placeholder}
                                name={data[0].name}
                                value={data[0].value}
                                type={data[0].type}
                                onChange={e => handleChange(0, e.target.value)}
                            />
                            )}
                            <div className={`flex justify-end items-center border ${active ? `border-primary` : `border-inactive`} rounded-md h-full min-h-12 min-w-36`}>
                                <label className="inline-flex items-center me-5 cursor-pointer h-full">
                                    <span className={`ms-3 text-base ${active ? `text-primary` : 'text-inactive'} font-bold dark:text-gray-300 mr-2`}>{active ? 'Active' : 'Inactive'}</span>
                                    <input type="checkbox" name={'active'} value={active} className="sr-only peer" onChange={() => setActive(!active)} checked={active} />
                                    <div className="relative w-11 h-6 bg-white border-black ring-1 ring-inactive rounded-full peer dark:bg-white peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:start-[2px] after:bg-inactive after:border-inactive peer-checked:after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-navBtn peer-checked:ring-primary peer-checked:bg-primary"></div>
                                </label>
                            </div>
                        </div>
                        {data && data[1] && (
                        <TextArea
                            minHeight={'180px'}
                            label={data[1].label}
                            placeholder={data[1].placeholder}
                            name={data[1].name}
                            value={data[1].value}
                            type={data[1].type}
                            onChange={e => handleChange(1, e.target.value)}
                        />
                        )}
                    </div>
                    {/* Category Logo Section */}
                    <div className="mt-0">
                        {/* <ImageInput
                            image={image}
                            setImage={setImage}
                            text={'Category Logo'}
                        /> */}
                        <MediaUpload
                            label="Category Logo"
                            id="categoryLogo"
                            setImageUpload={setImage}
                            existingImage={image}
                            stateImage={image}
                            setStateImageUpload={setImage}
                        />
                    </div>
                </div>

                <div className="flex mt-10 gap-3 items-center justify-between self-stretch my-auto whitespace-nowrap min-w-[240px] w-full">
                    <ActionButton variant="reset" text="Reset" handleSubmit={resetForm} />
                    <div className="flex gap-3">
                        <ActionButton variant="cancel" text="Cancel" handleSubmit={() => navigate('/help-desk/issue-categories')} />
                        <ActionButton variant="submit" text="Submit" handleSubmit={handleSubmit} />
                    </div>
                </div>
                
            </section>
        </form>
    <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none mt-4">

      {/* Header */}
      <h1 className="text-2xl font-bold text-[#2ECC40] mb-2">Help Desk- Issue Category List</h1>

      {/* Search Bar */}
      <div className="bg-white rounded-md shadow p-4 mb-4">
        <input
          type="text"
          placeholder="Search..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="flex-auto max-md:max-w-full bg-transparent border-none outline-none text-base"
        />
      </div>

      {/* Filters */}
      <form className="bg-white rounded-md shadow p-4 mb-4" onSubmit={e => { e.preventDefault(); }}>
        <div className="flex flex-wrap gap-4 w-full justify-between ">
          <div className=" min-w-[200px] shadow w-fit">
            <label className="block text-sm mb-1">Concern Status</label>
            <select
              name="status"
              value={filters.status}
              onChange={e => setFilters(f => ({ ...f, status: e.target.value }))}
              className="w-full rounded-md px-3 py-2 bg-white"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
          <div className="flex items-end gap-2">
            <button
              type="button"
              className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md border border-gray-200"
              onClick={() => {
                setFilters({ status: 'Active' });
                setSearch('');
              }}
            >
              Reset
            </button>
            <button
              type="submit"
              className="bg-[#2ECC40] text-white px-6 py-2 rounded-md hover:bg-green-600"
            >
              Search
            </button>
          </div>
        </div>
      </form>

      {/* Table Section */}
      <section className="bg-white rounded-md shadow p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="font-semibold">Help Desk- Issue Category List</div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <FaThLarge className="text-gray-400 cursor-pointer hover:text-gray-600" />
              <FaList className="text-gray-600 cursor-pointer" />
              <FaChartBar className="text-gray-400 cursor-pointer hover:text-gray-600" />
            </div>
            <select 
              className="border border-gray-200 rounded px-2 py-1 text-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1); // Reset to first page when changing page size
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <select className="border border-gray-200 rounded px-2 py-1 text-sm">
              <option>Sort</option>
              <option>Name A-Z</option>
              <option>Name Z-A</option>
              <option>Date</option>
            </select>
          </div>
        </div>
        <TanStackTable
          data={paginatedData}
          columns={columns}
          enablePagination={false}
          enableRowSelection={true}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
        />
        {/* Pagination and count */}
        <div className="flex items-center justify-between mt-4">
          <div className="text-sm text-gray-500">{(page - 1) * pageSize + 1} out of {filteredData.length}</div>
          <div className="flex gap-1">
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`px-3 py-1 rounded ${page === i + 1 ? 'bg-[#2ECC40] text-white' : 'bg-gray-100 text-gray-700'}`}
                onClick={() => setPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>
      </section>
      <ToastContainer 
        position="top-right" 
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </main> 
    </div>

) 
};

export default IssueCategoryList;